import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  CircularProgress,
  Divider,
  alpha,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useNavigate } from 'react-router-dom';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import axios from 'axios';
import { environment } from '../../../environments/environment';
import { styled, Theme } from '@mui/material/styles';

// Brand colors (consistent with pricing)
const BRAND_PRIMARY = '#2A9235';
const BRAND_SECONDARY = '#4CAF50';
const BG_TINT = '#f8fafc';

// Reuse the same heading styles used on Pricing/Resources so visual identity matches
const MainTitle = styled(Typography)(({ theme }: { theme: Theme }) => ({
  // Match Pricing page main title styling (weight/size)
  fontSize: '1.75rem',
  fontWeight: 700,
  marginBottom: theme.spacing(1.5),
  letterSpacing: '-0.02em',
  position: 'relative',
  background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
  WebkitTextFillColor: theme.palette.text.secondary,
  [theme.breakpoints.up('md')]: {
    fontSize: '2.25rem',
  },
}));

const SubTitle = styled(Typography)(({ theme }: { theme: Theme }) => ({
  // Match Pricing page subtitle styling (lighter weight)
  fontSize: '1rem',
  fontWeight: 400,
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
  marginTop: theme.spacing(0.5),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    fontSize: '1.125rem',
  },
}));

interface FormData {
  name: string;
  email: string;
  phone: string;
  schoolName: string;
  role: string; // free text role/title
  schoolType: string;
  studentCount: string;
  message: string;
}

const QuoteRequest: React.FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    schoolName: '',
    role: '',
    schoolType: '',
    studentCount: '',
    message: '',
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string>('');

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent
  ) => {
    const { name, value } = e.target as { name: keyof FormData; value: string };
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.schoolName.trim())
      newErrors.schoolName = 'School/District is required';
    if (!formData.schoolType) newErrors.schoolType = 'School type is required';
    if (!formData.studentCount)
      newErrors.studentCount = 'Student count is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    const status = document.getElementById('status-region');
    if (status) status.setAttribute('aria-busy', 'true');

    try {
      const response = await axios.post(
        `${environment.backendUrl}api/quote-request`,
        formData
      );
        if (response.status === 200) {
          // try {
          //   const notificationResponse = await axios.post(`${environment.apiUrl}sendQuoteNotification`, {
          //     email_type: 'quote_request',
          //   });
            
          //   if (notificationResponse.data && notificationResponse.data.status_code === 500) {
          //     console.warn('sendQuoteNotification failed with status_code 500:', notificationResponse.data);
          //     window.alert(notificationResponse.data.error_message || 'Failed to send quote email. Please try again after some time.');
          //     return;
          //   }
          // } catch (notifyErr) {
          //   console.warn('sendQuoteNotification failed', notifyErr);
          //   window.alert('Failed to send quote email. Please try again after some time.');
          //   return;
          // }

        setSubmitted(true);
        setSubmittedEmail(formData.email);
        if (status) {
          status.textContent = 'Your quote request was submitted successfully.';
          status.removeAttribute('aria-busy');
        }
        setFormData({
          name: '',
          email: '',
          phone: '',
          schoolName: '',
          role: '',
          schoolType: '',
          studentCount: '',
          message: '',
        });
      }
    } catch (error) {
      const status = document.getElementById('status-region');
      if (status) {
        status.textContent =
          'There was an error submitting the form. Please try again.';
        status.removeAttribute('aria-busy');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const formFieldStyles = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: '#E2E8F0' },
      '&:hover fieldset': { borderColor: BRAND_PRIMARY },
      '&.Mui-focused fieldset': { borderColor: BRAND_PRIMARY },
      backgroundColor: 'rgba(255,255,255,0.9)',
      fontSize: '16px',
    },
    '& .MuiInputLabel-root': {
      fontWeight: 600,
      fontSize: '14px',
    },
    '& .MuiInputLabel-root.Mui-focused': { color: BRAND_PRIMARY },
    '& .MuiInputBase-input': {
      fontSize: '16px',
    },
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          position: 'relative',
          overflowX: 'clip', // prevent horizontal scroll
          background: `radial-gradient(1100px 600px at 15% -10%, rgba(42,146,53,0.08), transparent 60%),
                     radial-gradient(900px 500px at 85% -10%, rgba(76,175,80,0.08), transparent 60%),
                     linear-gradient(135deg, ${BG_TINT} 0%, #ffffff 100%)`,
        }}
      >
        <Navbar currentPage="pricing" />

        {/* Match Pricing page vertical spacing so header sits at the same position */}
        <Box
          sx={{ flex: 1, pt: { xs: 10, md: 12 }, pb: { xs: 4, md: 8 }, px: 2 }}
        >
          <Container maxWidth="md">
            {/* Visually hidden live region that never contributes to layout */}
            <Box
              id="status-region"
              aria-live="polite"
              aria-atomic="true"
              sx={{
                position: 'absolute',
                width: '1px',
                height: '1px',
                padding: 0,
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0 0 0 0)',
                whiteSpace: 'nowrap',
                border: 0,
                top: 0,
                left: 0,
              }}
            />

            {/* Header: match the Pricing page placement and spacing */}
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <MainTitle variant="h2">Let’s Build Your School’s Plan</MainTitle>
              <SubTitle>
                Complete the short form below and receive a tailored quote
                within one business day.
              </SubTitle>
              <Box
                sx={{
                  width: 50,
                  height: 3,
                  background: `linear-gradient(90deg, ${BRAND_PRIMARY}, ${BRAND_SECONDARY})`,
                  borderRadius: 2,
                  mx: 'auto',
                  mb: 1,
                }}
              />
            </Box>

            {/* Main card */}
            <Paper
              elevation={3}
              sx={{
                // reduced padding to keep the form compact and fit better on one viewport
                p: { xs: 2, md: 3 },
                borderRadius: 4,
                background:
                  'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
                backdropFilter: 'blur(6px)',
                border: '1px solid rgba(226,232,240,0.8)',
                position: 'relative',
                overflow: 'visible', // allow internal spacing to flow naturally
              }}
            >
              {isSubmitting && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255,255,255,0.75)',
                    borderRadius: 4,
                    zIndex: 2,
                  }}
                >
                  <CircularProgress size={56} />
                </Box>
              )}

              {submitted ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 6,
                    px: 4,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
                    borderRadius: 3,
                    border: '2px solid rgba(42,146,53,0.1)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1), 0 0 0 1px rgba(42,146,53,0.05)',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: `linear-gradient(90deg, ${BRAND_PRIMARY} 0%, ${BRAND_SECONDARY} 100%)`,
                    },
                  }}
                >
                  {/* Success Icon */}
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'inline-flex',
                      mb: 4,
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${BRAND_PRIMARY}15, ${BRAND_SECONDARY}25)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        boxShadow: `0 8px 32px ${alpha(BRAND_PRIMARY, 0.2)}`,
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          inset: '-2px',
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${BRAND_PRIMARY}, ${BRAND_SECONDARY})`,
                          zIndex: -1,
                          opacity: 0.1,
                        },
                      }}
                    >
                      <CheckCircleIcon
                        sx={{
                          fontSize: 48,
                          color: BRAND_PRIMARY,
                          filter: 'drop-shadow(0 2px 8px rgba(42,146,53,0.3))',
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Success Message */}
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      fontSize: '1.75rem',
                      background: `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_SECONDARY} 100%)`,
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      color: 'transparent',
                      WebkitTextFillColor: 'transparent',
                      mb: 3,
                      letterSpacing: '-0.02em',
                      lineHeight: 1.2,
                      textAlign: 'center',
                      display: 'block',
                      width: '100%',
                    }}
                  >
                    Request Received Successfully
                  </Typography>

                  <Typography
                    sx={{
                      color: '#64748b',
                      fontSize: '1.1rem',
                      fontWeight: 500,
                      lineHeight: 1.6,
                      mb: 6,
                      maxWidth: '500px',
                      mx: 'auto',
                      textAlign: 'center',
                      display: 'block',
                      width: '100%',
                    }}
                  >
                    Thank you for your interest! Your custom quote request has
                    been submitted and is being processed by our team.
                  </Typography>

                  {/* Contact Assistance Section */}
                  <Box
                    sx={{
                      py: 4,
                      px: 4,
                      backgroundColor: 'rgba(42,146,53,0.03)',
                      borderRadius: 2,
                      border: '1px solid rgba(42,146,53,0.1)',
                      mb: 4,
                      display: 'block',
                      width: '100%',
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#374151',
                        fontSize: '1rem',
                        fontWeight: 600,
                        mb: 1,
                      }}
                    >
                      Need immediate assistance?
                    </Typography>
                    <Typography
                      sx={{
                        color: BRAND_PRIMARY,
                        fontSize: '1rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      sales@pragnatyx.com
                    </Typography>
                  </Box>

                  {/* Action Buttons */}
                  <Box
                    sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      justifyContent: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={() => navigate('/')}
                      sx={{
                        px: 4,
                        py: 1.5,
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '1rem',
                        minWidth: '180px',
                        background: `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_SECONDARY} 100%)`,
                        boxShadow: '0 4px 16px rgba(42,146,53,0.3)',
                        '&:hover': {
                          background: `linear-gradient(135deg, #1e7a2b 0%, #43a047 100%)`,
                          boxShadow: '0 6px 20px rgba(42,146,53,0.4)',
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      Back to Homepage
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => navigate('/pricing')}
                      sx={{
                        px: 4,
                        py: 1.5,
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '1rem',
                        minWidth: '180px',
                        borderColor: BRAND_PRIMARY,
                        borderWidth: '2px',
                        color: BRAND_PRIMARY,
                        '&:hover': {
                          borderColor: BRAND_PRIMARY,
                          backgroundColor: `${BRAND_PRIMARY}08`,
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 16px rgba(42,146,53,0.2)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      View Pricing
                    </Button>
                  </Box>
                </Box>
              ) : (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#000000',
                        mb: 1,
                      }}
                    >
                      1. Your Contact Information
                    </Typography>
                    <Divider />
                  </Box>

                  <form onSubmit={handleSubmit} noValidate>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Your Full Name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          error={!!errors.name}
                          helperText={errors.name}
                          required
                          variant="outlined"
                          sx={formFieldStyles}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Your Work Email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          error={!!errors.email}
                          helperText={errors.email}
                          required
                          variant="outlined"
                          sx={formFieldStyles}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Your Role/Title (e.g., Teacher, IT Admin, Principal)"
                          name="role"
                          value={formData.role}
                          onChange={handleInputChange}
                          variant="outlined"
                          sx={formFieldStyles}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl
                          fullWidth
                          error={!!errors.studentCount}
                          required
                          sx={formFieldStyles}
                        >
                          <InputLabel>Estimated Number of Students</InputLabel>
                          <Select
                            name="studentCount"
                            value={formData.studentCount}
                            label="Estimated Number of Students"
                            onChange={handleInputChange}
                          >
                            <MenuItem value="1-50">1-50</MenuItem>
                            <MenuItem value="51-100">51-100</MenuItem>
                            <MenuItem value="101-500">101-500</MenuItem>
                            <MenuItem value="501-1000">501-1000</MenuItem>
                            <MenuItem value="1001-5000">1001-5000</MenuItem>
                            <MenuItem value="5000+">5000+</MenuItem>
                          </Select>
                          {errors.studentCount && (
                            <FormHelperText>
                              {errors.studentCount}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="School or District Name"
                          name="schoolName"
                          value={formData.schoolName}
                          onChange={handleInputChange}
                          error={!!errors.schoolName}
                          helperText={errors.schoolName}
                          required
                          variant="outlined"
                          sx={formFieldStyles}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl
                          fullWidth
                          error={!!errors.schoolType}
                          required
                          sx={formFieldStyles}
                        >
                          <InputLabel>School Type</InputLabel>
                          <Select
                            name="schoolType"
                            value={formData.schoolType}
                            label="School Type"
                            onChange={handleInputChange}
                          >
                            <MenuItem value="Elementary School">
                              Elementary School
                            </MenuItem>
                            <MenuItem value="Middle School">
                              Middle School
                            </MenuItem>
                            <MenuItem value="High School">High School</MenuItem>
                            <MenuItem value="K-12 School">K-12 School</MenuItem>
                            <MenuItem value="School District">
                              School District
                            </MenuItem>
                            <MenuItem value="Other">Other</MenuItem>
                          </Select>
                          {errors.schoolType && (
                            <FormHelperText>{errors.schoolType}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <Box sx={{ mt: 1, mb: 1.5 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: '16px',
                              fontWeight: 600,
                              color: '#000000',
                              mb: 1,
                            }}
                          >
                            3. Share any details to help us prepare your quote
                          </Typography>
                          <Divider />
                        </Box>
                        <TextField
                          fullWidth
                          label="Provide details to help us prepare your quote"
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          multiline
                          rows={4}
                          placeholder='e.g. "We need to use a purchase order for 1,000 students in Grades 1–3, starting September 1"'
                          variant="outlined"
                          sx={formFieldStyles}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          mt: 1.5,
                        }}
                      >
                        <Button
                          type="submit"
                          variant="contained"
                          endIcon={<SendIcon />}
                          disabled={isSubmitting}
                          sx={{
                            color: '#fff',
                            borderRadius: 999,
                            px: 4,
                            py: 1.6,
                            textTransform: 'none',
                            fontWeight: 900,
                            minWidth: 200,
                            background: `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_SECONDARY} 100%)`,
                            boxShadow: '0 8px 24px rgba(42,146,53,0.25)',
                            '&:hover': {
                              background: `linear-gradient(135deg, #1e7a2b 0%, #43a047 100%)`,
                              boxShadow: '0 10px 28px rgba(42,146,53,0.35)',
                            },
                          }}
                        >
                          {isSubmitting ? 'Submitting…' : 'Get My Custom Quote'}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>

                  <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#2A9235',
                        mb: 1,
                      }}
                    >
                      What Happens Next?
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#555',
                        lineHeight: 1.4,
                      }}
                    >
                      Our team will review your request and send a detailed
                      quote to your email within one business day.
                    </Typography>
                  </Box>
                </>
              )}
            </Paper>
          </Container>
        </Box>
      </Box>
      <Footer />
    </>
  );
};

export default QuoteRequest;
